import {
    ConditionBlock,
    ConditionIf,
    CWTRef,
    CWTSchemaAggregation,
    CWTSchemaCalculation,
    CWTSchemaCondition,
    CWTSchemaExpression,
    CWTSchemaFunction,
    FieldType,
    WorksheetSchema,
    WorksheetSchemaListItem,
    WorksheetSchemaTableRow,
} from '../../../../types/custom-worksheet-v2';
import {
    getField,
    getFieldValue,
    getProperty,
    isCalculation,
    isConditionAnd,
    isConditionOr,
    isField,
    isInCollection,
    isRef,
    isSection,
    isTable,
    isTableRow,
    setFieldValue,
} from './utils';
import * as formula from '@formulajs/formulajs';
import traverse from 'traverse';
import { get } from 'lodash';
import { getReference } from '../../../../components/custom-worksheet/utils';

/**
 * Class to run calculations over a provided schema, updating new field values.
 */
export class Calculator {
    schema: WorksheetSchema;

    constructor(schema: WorksheetSchema) {
        this.schema = structuredClone(schema);
    }

    /**
     * Runs the calculation for the given field and its dependent fields.
     *
     * @param id - The id of the field to calculate.
     * @returns The updated worksheet schema.
     */
    run(id: string): WorksheetSchema {
        if (!id) {
            return this.schema;
        }
        const property = getProperty(this.schema, id);

        if (!property) {
            return this.schema;
        }

        if (!('calculation' in property)) {
            let schema = this.schema;
            const calculator = this;

            traverse(property.properties).forEach(function (x) {
                if (isField(x)) {
                    schema = calculator.run(x.id);
                }
            });

            // Check if it also has a summary
            if (isTable(property)) {
                if (property.summary) {
                    for (const key in property.summary.properties) {
                        const prop = property.summary.properties[key];
                        if (prop) {
                            schema = calculator.run(prop.id);
                        }
                    }
                }

                if (property.dependentFields && property.dependentFields.length > 0) {
                    property.dependentFields.forEach((df) => {
                        console.log('RUNNING...');
                        this.schema = this.run(df.ref.substring(1));
                    });
                }
            }

            if (isSection(property) && property.summary) {
                const total = property.summary.properties[getReference(property.id)];
                if (total) {
                    schema = calculator.run(total.id);
                }
            }

            this.schema = schema;
        } else {
            const field = property;

            // Run its own calculations
            if (field.calculation && !field.overridden) {
                const newValue = this.runCalculation(field.calculation);

                if (newValue !== field.value) {
                    this.schema = setFieldValue(this.schema, field.id, newValue);
                }
            }

            // If the field is in a table check if the table has any dependent fields
            const inTable = isInCollection(this.schema, field.id);

            if (inTable && isTable(inTable) && inTable.dependentFields) {
                // Check the dependentFields
                inTable.dependentFields.forEach((df) => {
                    if (df.ref.includes('.__every__.')) {
                        this.handleAggregationDependency(df);
                        return;
                    }

                    const dependentField = getField(this.schema, df);

                    if (!dependentField) {
                        return;
                    }

                    // Don't run calculations on overridden fields
                    if (dependentField.overridden) {
                        return;
                    }
                    const calculation = dependentField.calculation;
                    if (!calculation) {
                        return;
                    }

                    const newValue = this.runCalculation(calculation);

                    if (newValue !== dependentField.value) {
                        this.schema = setFieldValue(this.schema, dependentField.id, newValue);
                        // If it has dependent fields repeat this process
                        if (dependentField.dependentFields.length > 0) {
                            this.schema = this.run(dependentField.id);
                        }
                    }
                });
            }

            // Check the dependentFields
            field.dependentFields.forEach((df) => {
                if (df.ref.includes('.__every__.')) {
                    this.handleAggregationDependency(df);
                    return;
                }

                const dependentField = getField(this.schema, df);

                if (!dependentField) {
                    return;
                }

                // Don't run calculations on overridden fields
                if (dependentField.overridden) {
                    return;
                }
                const calculation = dependentField.calculation;
                if (!calculation) {
                    return;
                }

                const newValue = this.runCalculation(calculation);

                if (newValue !== dependentField.value) {
                    this.schema = setFieldValue(this.schema, dependentField.id, newValue);
                    // If it has dependent fields repeat this process
                    if (dependentField.dependentFields.length > 0) {
                        this.schema = this.run(dependentField.id);
                    }
                }
            });
        }
        return this.schema;
    }

    private handleAggregationDependency(df: CWTRef): void {
        // Get the collection
        const parts = df.ref.substring(1).split('.__every__.');

        if (parts.length === 2) {
            const cId = parts[0];
            const pId = parts[1];

            const properties = get(this.schema, cId);

            const ids: string[] = [];

            if (Array.isArray(properties)) {
                for (const prop of properties) {
                    const field = get(prop, pId as string);
                    ids.push(field.id);
                }
            }

            ids.forEach((id) => {
                this.schema = this.run(id);
            });
        } else {
            // Its three list -> table -> column
            const listItems = get(this.schema, parts[0]) as WorksheetSchemaListItem[];
            if (!listItems) {
                return;
            }
            const ids = listItems.map((item) => item.id + '.' + parts[1] + '.__every__.' + parts[2]);

            for (const id of ids) {
                const parts = id.split('.__every__.');

                const cId = parts[0];
                const pId = parts[1];

                const properties = get(this.schema, cId);

                const ids: string[] = [];

                if (Array.isArray(properties)) {
                    for (const prop of properties) {
                        const field = get(prop, pId as string);
                        ids.push(field.id);
                    }
                }

                ids.forEach((id) => {
                    this.schema = this.run(id);
                });
            }
        }
    }

    /**
     * Runs calculation logic
     * @param calculation - The calculation object to evaluate
     */
    private runCalculation(calculation: CWTSchemaCalculation | CWTRef): unknown {
        if (isRef(calculation)) {
            return getFieldValue(this.schema, calculation);
        }

        switch (calculation.type) {
            case 'CONDITION':
                return this.runCondition(calculation);

            case 'EXPRESSION':
                return this.runExpression(calculation);

            case 'FUNCTION':
                return this.runFunction(calculation);
            case 'AGGREGATION':
                return this.runAggregation(calculation);
            default:
                return null;
        }
    }

    runAggregation(calculation: CWTSchemaAggregation): unknown {
        const ref = calculation.return.params[0] as CWTRef;

        const split = ref.ref.split('.__every__.');
        const propertiesId = split[0].substring(1);
        const propertyId = split[1];
        const properties = get(this.schema, propertiesId) as WorksheetSchemaTableRow[];

        const values = properties.map((p) => {
            return get(p, propertyId)?.value ?? null;
        });

        return this.runFunction({ type: 'FUNCTION', return: { operation: calculation.return.operation, params: values } });
    }

    /**
     * Runs condition logic
     * @param condition - The condition object to evaluate
     */
    runCondition(condition: CWTSchemaCondition): unknown {
        const { if: conIf, then, else: conElse } = condition.return;

        const ifRes = this.evaluateIfStatement(conIf);
        // Evaluate the if condition is truthy
        if (ifRes === null) {
            return null;
        } else if (ifRes) {
            // Evaluate the "then" logic
            return this.evaluateBlockStatement(then);
        } else {
            return this.evaluateBlockStatement(conElse);
        }
    }

    /**
     * Runs expression logic
     * @param expression - The expression object to evaluate
     */
    runExpression(expression: CWTSchemaExpression): unknown {
        const { operation, params } = expression.return;
        if (params.length !== 2) {
            return null;
        }

        const vals = params.map((p) => {
            if (isCalculation(p) || isRef(p)) {
                return this.runCalculation(p);
            }
            return p;
        });

        if (typeof vals[0] !== 'number' || typeof vals[1] !== 'number') {
            return null;
        }

        switch (operation) {
            case '+':
                return vals[0] + vals[1];
            case '-':
                return +vals[0] - +vals[1];
            case '*':
                return +vals[0] * +vals[1];
            case '/':
                // Prevent a divide by 0 error
                if (+vals[1] === 0) {
                    return 0;
                }
                return +vals[0] / +vals[1];
            case '^':
                return (+vals[0]) ** +vals[1];

            default:
                return null;
        }
    }

    /**
     * Runs function logic
     * @param _function - The function object to evaluate
     */
    runFunction(_function: CWTSchemaFunction): unknown {
        let params: (string | number | null | CWTRef)[] = [];
        if (_function.return.operation === 'FIND' || _function.return.operation === 'FILTER') {
            const [tableId, matchColRef, val, rtnColRef] = _function.return.params as [CWTRef, string, string | number | null, string];
            const resolvedVal = isCalculation(val) || isRef(val) ? this.runCalculation(val) : val;
            // @ts-ignore
            params = [tableId, matchColRef, resolvedVal, rtnColRef];
        } else {
            // @ts-ignore
            params = _function.return.params.map((p) => {
                if (isCalculation(p) || isRef(p)) {
                    return this.runCalculation(p);
                }
                return p;
            });
        }

        if (params.length === 0) {
            return null;
        }

        const operation = _function.return.operation;

        if (params.some((p) => p === null) && operation !== 'IS_NULL') {
            return null;
        }

        switch (operation) {
            case 'SUM':
            case 'MAX':
            case 'MIN':
            case 'AVERAGE': {
                if (params.some((p) => typeof p !== 'number')) {
                    return null;
                }
                const func = this.getFunctionDefinition(operation);
                if (!func) {
                    return null;
                }
                const res = func(...params);

                if (res instanceof Error) {
                    return null;
                }

                return res;
            }

            case 'COUNT':
            case 'CONCAT': {
                const func = this.getFunctionDefinition(operation);
                if (!func) {
                    return null;
                }
                const res = func(...params);

                if (res instanceof Error) {
                    return null;
                }

                return res;
            }

            case 'ROUND':
            case 'ROUND_UP':
            case 'ROUND_DOWN': {
                if (params.some((p) => typeof p !== 'number')) {
                    return null;
                }

                let decimalPlaces = 2;
                if (params.length > 1) {
                    decimalPlaces = (params?.at(-1) as number) ?? 2;
                }

                const func = this.getFunctionDefinition(operation);
                if (!func) {
                    return null;
                }
                const res = func(params[0], decimalPlaces);

                if (res instanceof Error) {
                    return null;
                }

                return res;
            }

            case 'ABS':
            case 'SQRT':
            case 'CEILING':
            case 'FLOOR':
            case 'NEGATIVE': {
                if (typeof params[0] !== 'number') {
                    return null;
                }
                const func = this.getFunctionDefinition(operation);
                if (!func) {
                    return null;
                }
                const res = func(params[0]);

                if (res instanceof Error) {
                    return null;
                }

                return res;
            }
            case 'DAYS_BETWEEN':
            case 'IDAYS_BETWEEN':
            case 'MONTHS_BETWEEN':
            case 'YEARS_BETWEEN': {
                if (params.some((p) => typeof p !== 'string')) {
                    return null;
                }

                const func = this.getFunctionDefinition(operation);

                if (!func) {
                    return null;
                }
                const res = func(params[0], params[1]);
                if (res instanceof Error) {
                    return null;
                }

                return res;
            }
            case 'DAY':
            case 'MONTH':
            case 'YEAR': {
                if (params.some((p) => typeof p !== 'string')) {
                    return null;
                }

                const func = this.getFunctionDefinition(operation);

                if (!func) {
                    return null;
                }
                const res = func(params[0]);
                if (res instanceof Error) {
                    return null;
                }

                return res;
            }
            case 'ADD_YEARS':
            case 'MINUS_YEARS':
            case 'ADD_MONTHS':
            case 'MINUS_MONTHS':
            case 'ADD_DAYS':
            case 'MINUS_DAYS': {
                if (typeof params[0] !== 'string' || typeof params[1] !== 'number') {
                    return null;
                }
                const func = this.getFunctionDefinition(operation);

                if (!func) {
                    return null;
                }

                const res = func(params[0], params[1]);
                if (res instanceof Error) {
                    return null;
                }

                const dateString = new Date(res.getTime() - res.getTimezoneOffset() * 60000).toISOString().split('T')[0];
                return dateString;
            }
            case 'DATE': {
                if (params.some((p) => typeof p !== 'number')) {
                    return null;
                }
                const func = this.getFunctionDefinition(operation);

                if (!func) {
                    return null;
                }

                const res = func(params[0], params[1], params[2]);
                if (res instanceof Error) {
                    return null;
                }

                const dateString = new Date(res.getTime() - res.getTimezoneOffset() * 60000).toISOString().split('T')[0];
                return dateString;
            }

            case 'EOMONTH': {
                const func = this.getFunctionDefinition(operation);

                if (!func) {
                    return null;
                }

                const res = func(params[0], params[1] ?? 0);
                if (res instanceof Error) {
                    return null;
                }

                const dateString = new Date(res.getTime() - res.getTimezoneOffset() * 60000).toISOString().split('T')[0];
                return dateString;
            }

            case 'IS_NULL': {
                const func = this.getFunctionDefinition(operation);

                if (!func) {
                    return null;
                }

                const res = func(params[0]);

                if (res instanceof Error) {
                    return null;
                }

                return res;
            }
            case 'PPMT': {
                if (params.length < 4 || !params.every((p) => typeof p === 'number' || typeof p === 'undefined')) {
                    return null;
                }
                const func = this.getFunctionDefinition(operation);

                if (!func) {
                    return null;
                }

                const res = func(...params);

                if (res instanceof Error) {
                    return null;
                }

                return res;
            }
            case 'PMT': {
                if (params.length < 3 || !params.every((p) => typeof p === 'number' || typeof p === 'undefined')) {
                    return null;
                }
                const func = this.getFunctionDefinition(operation);

                if (!func) {
                    return null;
                }

                const res = func(...params);

                if (res instanceof Error) {
                    return null;
                }

                return res;
            }
            case 'SUM_LOANINT':
            case 'SUM_CBAL': {
                if (params.length < 4 || !params.every((p) => typeof p === 'number' || typeof p === 'undefined')) {
                    return null;
                }

                const func = this.getFunctionDefinition(operation);

                if (!func) {
                    return null;
                }

                const res = func(...params);

                if (res instanceof Error) {
                    return null;
                }

                return res;
            }
            case 'FINANCIAL_YEAR_END': {
                if (
                    params.length !== 3 ||
                    typeof params[0] !== 'string' ||
                    typeof params[1] !== 'number' ||
                    typeof params[2] !== 'number'
                ) {
                    return null;
                }

                const func = this.getFunctionDefinition(operation);

                if (!func) {
                    return null;
                }

                const res = func(...params);

                if (res instanceof Error) {
                    return null;
                }

                return res;
            }
            case 'TAX_AU': {
                if (params.length !== 2 || typeof params[0] !== 'number' || typeof params[1] !== 'string') {
                    return null;
                }

                const func = this.getFunctionDefinition(operation);

                if (!func) {
                    return null;
                }

                const res = func(...params);

                if (res instanceof Error) {
                    return null;
                }

                return res;
            }
            case 'DIV_7A_RATE': {
                if (params.length !== 1 || typeof params[0] !== 'string') {
                    return null;
                }

                const func = this.getFunctionDefinition(operation);

                if (!func) {
                    return null;
                }

                const res = func(...params);

                if (res instanceof Error) {
                    return null;
                }

                return res;
            }
            case 'RATE': {
                if (params.length !== 6 || params.some((p) => typeof p !== 'number')) {
                    return null;
                }

                const func = this.getFunctionDefinition(operation);

                if (!func) {
                    return null;
                }

                const res = func(...params);

                if (res instanceof Error) {
                    return null;
                }

                return res;
            }

            case 'FIND':
            case 'FILTER': {
                if (
                    params.length !== 4 ||
                    typeof params[0] !== 'object' ||
                    typeof params[1] !== 'string' ||
                    !params[2] ||
                    typeof params[3] !== 'string'
                ) {
                    return null;
                }
                const func = this.getFunctionDefinition(operation);
                if (!func) {
                    return null;
                }
                const res = func(...params);
                if (res instanceof Error) {
                    return null;
                }
                return res;
            }

            default:
                return null;
        }
    }

    /**
     * Evaluates an if statement.
     * @param condition - The if statement to evaluate.
     * @returns The result of the evaluation.
     */
    private evaluateIfStatement(condition: ConditionIf): boolean | null {
        if (isConditionAnd(condition) || isConditionOr(condition)) {
            let hasNull = false;

            /**
             * This temporary function runs for both and and or evalution
             * @param andor ConditionIf
             */
            const check = (andor: ConditionIf): boolean | null => {
                const res = this.evaluateIfStatement(andor);
                if (res === null) {
                    hasNull = true;
                }
                return res;
            };

            // If it's an `and` we check if every condition is met, else for an `or` we check some
            const result = isConditionAnd(condition) ? condition.and.every(check) : condition.or.some(check);
            return hasNull ? null : result;
        }

        const { operation, lhs, rhs } = condition;

        const vals = [lhs, rhs].map((v) => {
            // Check if it's another calculation
            if (isCalculation(v) || isRef(v)) {
                return this.runCalculation(v);
            }
            // It's just a primitive
            return v;
        });

        const lhsVal = vals[0];
        const rhsVal = vals[1];

        if (lhsVal == null || rhsVal == null) {
            return null;
        }

        switch (operation) {
            case '=':
                return lhsVal === rhsVal;
            case '!=':
                return lhsVal !== rhsVal;
            case '>': {
                return lhsVal > rhsVal;
            }
            case '<': {
                return lhsVal < rhsVal;
            }
            case '>=': {
                return lhsVal >= rhsVal;
            }
            case '<=': {
                return lhsVal <= rhsVal;
            }
            default:
                return null;
        }
    }

    /**
     * Evaluates a block statement.
     *
     * @param block - The block statement to evaluate.
     * @returns The result of the evaluation.
     */
    private evaluateBlockStatement(block: ConditionBlock): unknown {
        if (isCalculation(block.return) || isRef(block.return)) {
            return this.runCalculation(block.return);
        }
        return block.return;
    }

    /**
     * Gets a predefined function by its id
     * @param id function id
     * @returns function
     */
    private getFunctionDefinition(id: string): Function | null {
        const defs = {
            /** Returns a sum of a collection of numbers */
            SUM: (...params: number[]): number => params.reduce((p, acc) => +p + +acc, 0),
            /** Returns the average of a collection of numbers */
            AVERAGE: (...params: number[]): number => defs.SUM(...params) / params.length || 1,
            /** Returns the highest value in a collection of numbers */
            MAX: Math.max,
            /** Returns the lowest value in a collection of numbers */
            MIN: Math.min,
            /** Returns the absolute value of a number */
            ABS: Math.abs,
            /** Returns a number rounded to the provided decimal places (defaults to 2) */
            ROUND: formula.ROUND,
            /** Returns a number rounded up to the provided decimal places (defaults to 2) */
            ROUND_UP: formula.ROUNDUP,
            /** Returns a number rounded down to the provided decimal places (defaults to 2) */
            ROUND_DOWN: formula.ROUNDDOWN,
            /** Returns a number rounded up to the nearest integer */
            CEILING: Math.ceil,
            /** Returns a number rounded down to the nearest integer */
            FLOOR: Math.floor,
            /** Returns the number of items in a collection */
            COUNT: (...params: unknown[]): number => params.length,
            /** Returns the square root of a number */
            SQRT: Math.sqrt,
            /** Returns the negative of a number */
            NEGATIVE: (v: number): number => (v <= 0 ? v : v * -1),
            /** Returns a single string separating each collection item by a space */
            CONCAT: (...params: (string | number)[]): string => {
                return params.slice(1).join(params[0]?.toString() || '');
            },

            /** Returns the number of days between two dates */
            DAYS_BETWEEN: (startDate: string, endDate: string): number | Error => formula.DATEDIF(startDate, endDate, 'D'),

            /** Returns the number of days between two dates (inclusive) */
            IDAYS_BETWEEN: (startDate: string, endDate: string): number | Error => {
                const r = formula.DATEDIF(startDate, endDate, 'D');
                return typeof r === 'number' ? r + 1 : r;
            },

            /** Returns the number of months between two dates */
            MONTHS_BETWEEN: (startDate: string, endDate: string): number | Error => formula.DATEDIF(startDate, endDate, 'M'),

            /** Returns the number of years between two dates */
            YEARS_BETWEEN: (startDate: string, endDate: string): number | Error => formula.DATEDIF(startDate, endDate, 'Y'),

            /** Returns the day value from a date string */
            DAY: formula.DAY,
            /** Returns the month value from a date string */
            MONTH: formula.MONTH,
            /** Returns the year value from a date string */
            YEAR: formula.YEAR,
            /** Adds years to the given date */
            ADD_YEARS: (date: string, years: number): Date | Error => {
                return formula.DATE(formula.YEAR(date) + years, formula.MONTH(date), formula.DAY(date));
            },
            /** Minus years to given date */
            MINUS_YEARS: (date: string, years: number): Date | Error => defs.ADD_YEARS(date, -years),
            /** Adds months to the given date */
            ADD_MONTHS: (date: string, months: number): Date | Error => {
                return formula.EDATE(date, months);
            },

            /** Minus months to given date */
            MINUS_MONTHS: (date: string, months: number): Date | Error => defs.ADD_MONTHS(date, -months),

            /** Adds days to the given date */
            ADD_DAYS: (date: string, days: number): Date | Error => {
                return formula.DATE(formula.YEAR(date), formula.MONTH(date), formula.DAY(date) + days);
            },

            /** Minus days to given date */
            MINUS_DAYS: (date: string, days: number): Date | Error => defs.ADD_DAYS(date, -days),

            /** Date construction */
            DATE: (year: number, month: number, day: number): Date | Error => formula.DATE(year, month, day),

            /** Get end of month date */
            EOMONTH: (date: string, months: number): Date | Error => formula.EOMONTH(date, months),

            /** Checks if a value is null */
            IS_NULL: (val: unknown): boolean => val == null,
            /** Returns the payment for a loan based on constant payments and a constant interest rate. */
            PMT: formula.PMT,
            /** Returns the payment on the principal for a given period for an investment based on periodic, constant payments and a constant interest rate. */
            PPMT: formula.PPMT,
            /**
             * Returns the sum of the total of interest on a principal loan (p) based on an interest rate of (i), for a term of (n) and repayments of (y).
             * @param principal
             * @param interest
             * @param term
             * @param repayment
             * @param type 0 for payments in arrears 1 for payments in advanced
             * @returns Total interest
             */
            SUM_LOANINT(principal: number, interest: number, term: number, repayment: number, type: 0 | 1 = 0): number {
                let loanBal = principal;
                let loanInt = 0;
                let sumInt = 0;

                for (let i = 0; i < term; i++) {
                    loanInt = type === 1 && i === 0 ? 0 : loanBal * interest;
                    loanBal = loanBal + loanInt - repayment;
                    sumInt += loanInt;
                }

                return sumInt;
            },
            /** Returns the closing balance on a principal loan (p) based on an interest rate of (i), for a term of (n) and repayments of (y).
             *
             * @param principal
             * @param interest
             * @param term
             * @param repayment
             * @param type
             * @returns closing balance
             */
            SUM_CBAL(principal: number, interest: number, term: number, repayment: number, type: 0 | 1 = 0): number {
                let loanBal = principal;
                let loanInt = 0;
                let sumInt = 0;

                for (let i = 0; i < term; i++) {
                    loanInt = type === 1 && i === 0 ? 0 : loanBal * interest;
                    loanBal = loanBal + loanInt - repayment;
                    sumInt += loanInt;
                }

                return loanBal;
            },
            FINANCIAL_YEAR_END(date: string, month: number, day: number): string {
                const parsedDate = new Date(date);
                const dYear = parsedDate.getFullYear();
                const dMonth = parsedDate.getMonth() + 1;
                const dDay = parsedDate.getDate();

                let year = dYear;

                if (dMonth > month) {
                    year = year + 1;
                }
                if (dMonth === month) {
                    if (dDay > day) {
                        year = year + 1;
                    }
                }

                return `${year}-${month < 10 ? `0${month}` : month}-${day < 10 ? `0${day}` : day}`;
            },

            TAX_AU(amount: number, date: string): number {
                // https://www.ato.gov.au/tax-rates-and-codes/tax-rates-australian-residents
                const FYEAR_END_MONTH = 6;
                const FYEAR_END_DAY = 30;
                const fiscalYear = defs.YEAR(defs.FINANCIAL_YEAR_END(date, FYEAR_END_MONTH, FYEAR_END_DAY));

                const res = (): number => {
                    switch (fiscalYear) {
                        case 2025: {
                            if (amount <= 18_200 * 100) return 0;
                            if (amount <= 45_000 * 100) return (amount - 18_200 * 100) * 0.16;
                            if (amount <= 135_000 * 100) return (amount - 45_000 * 100) * 0.3 + 4288 * 100;
                            if (amount <= 190_000 * 100) return (amount - 135_000 * 100) * 0.37 + 31_288 * 100;
                            else return (amount - 190_000 * 100) * 0.45 + 51_638 * 100;
                        }
                        case 2024:
                        case 2023:
                        case 2022:
                        case 2021: {
                            if (amount <= 18_200 * 100) return 0;
                            if (amount <= 45_000 * 100) return (amount - 18_200 * 100) * 0.19;
                            if (amount <= 120_000 * 100) return (amount - 45_000 * 100) * 0.325 + 5092 * 100;
                            if (amount <= 180_000 * 100) return (amount - 120_000 * 100) * 0.37 + 29_467 * 100;
                            else return (amount - 180_000 * 100) * 0.45 + 51_667 * 100;
                        }
                        case 2020:
                        case 2019:
                            if (amount <= 18_200 * 100) return 0;
                            if (amount <= 37_000 * 100) return (amount - 18_200 * 100) * 0.19;
                            if (amount <= 90_000 * 100) return (amount - 37_000 * 100) * 0.325 + 3572 * 100;
                            if (amount <= 180_000 * 100) return (amount - 90_000 * 100) * 0.37 + 20_797 * 100;
                            else return (amount - 180_000 * 100) * 0.45 + 54_097 * 100;
                        case 2018:
                        case 2017:
                            if (amount <= 18_200 * 100) return 0;
                            if (amount <= 37_000 * 100) return (amount - 18_200 * 100) * 0.19;
                            if (amount <= 87_000 * 100) return (amount - 37_000 * 100) * 0.325 + 3572 * 100;
                            if (amount <= 180_000 * 100) return (amount - 87_000 * 100) * 0.37 + 19_822 * 100;
                            else return (amount - 180_000 * 100) * 0.45 + 54_232 * 100;
                        case 2016:
                        case 2015:
                        case 2014:
                        case 2013:
                            if (amount <= 18_200 * 100) return 0;
                            if (amount <= 37_000 * 100) return (amount - 18_200 * 100) * 0.19;
                            if (amount <= 80_000 * 100) return (amount - 37_000 * 100) * 0.325 + 3572 * 100;
                            if (amount <= 180_000 * 100) return (amount - 80_000 * 100) * 0.37 + 17_547 * 100;
                            else return (amount - 180_000 * 100) * 0.45 + 54_547 * 100;
                    }

                    return 0;
                };

                return Math.ceil(res());
            },
            DIV_7A_RATE(date: string): number {
                // https://www.ato.gov.au/tax-rates-and-codes/division-7a-benchmark-interest-rate#ato-Benchmarkinterestrates
                const FYEAR_END_MONTH = 6;
                const FYEAR_END_DAY = 30;
                const fiscalYear = defs.YEAR(defs.FINANCIAL_YEAR_END(date, FYEAR_END_MONTH, FYEAR_END_DAY));

                // Change these values when a new rate is provided for the next year
                const currentYear = 2025;
                const currentRate = 0.0877;

                switch (fiscalYear) {
                    case 2025:
                        return 0.0877;
                    case 2024:
                        return 0.0827;
                    case 2023:
                        return 0.0477;
                    case 2022:
                    case 2021:
                        return 0.0452;
                    case 2020:
                        return 0.0537;
                    case 2019:
                        return 0.052;
                    case 2018:
                        return 0.053;
                    case 2017:
                        return 0.054;
                    case 2016:
                        return 0.0545;
                    case 2015:
                        return 0.0595;
                    default:
                        if (fiscalYear > currentYear) {
                            return currentRate;
                        }
                        return 0;
                }
            },

            RATE: formula.RATE,

            /**
             * @param tableId Full id of table to find
             * @param matchColRef The reference of the column to match on
             * @param val  The value to match on
             * @param rtnColRef The reference of the column whos value to return
             * @returns FieldValue
             */
            FIND: (tableId: CWTRef, matchColRef: string, val: string | number | null, rtnColRef: string): string | number | null => {
                const table = getProperty(this.schema, tableId.ref.substring(1));

                if (!table || !isTable(table)) {
                    return null;
                }

                for (const row of table.properties) {
                    const match = row.properties[matchColRef];
                    if (!match) {
                        continue;
                    }
                    if (match.value === val) {
                        const rtnCol = row.properties[rtnColRef];
                        if (rtnCol) {
                            return rtnCol.value;
                        }
                    }
                }
                return null;
            },
            /**
             * @param tableId Full id of table to find
             * @param matchColRef The reference of the column to match on
             * @param val  The value to match on
             * @param rtnColRef The reference of the column whos value to return
             * @returns FieldValue
             */
            FILTER: (tableId: CWTRef, matchColRef: string, val: string | number | null, rtnColRef: string): string | number | null => {
                const table = getProperty(this.schema, tableId.ref.substring(1));
                if (!table || !isTable(table)) {
                    return null;
                }

                // Check the value type of the rtnColRef
                const tmpRtnCol = table.row.properties[rtnColRef];
                if (!tmpRtnCol) {
                    return null;
                }

                const numerics = [FieldType.MONEY, FieldType.ACCOUNTING, FieldType.NUMBER, FieldType.PERCENT];

                let total: string | number | null = numerics.includes(tmpRtnCol.type) ? 0 : null;

                for (const row of table.properties) {
                    const match = row.properties[matchColRef];
                    if (!match) {
                        continue;
                    }
                    if (match.value === val) {
                        const rtnCol = row.properties[rtnColRef];
                        if (rtnCol) {
                            if (typeof total === 'number') {
                                total += typeof rtnCol.value === 'number' ? rtnCol.value : 0;
                            } else {
                                total = rtnCol.value;
                            }
                        }
                    }
                }
                return total;
            },
        };

        if (id in defs) {
            return defs[id as keyof typeof defs];
        }
        return null;
    }
}
